/**
 * TEXTOS DE AYUDA del panel — TODOS en un solo lugar, en español llano (es-DO),
 * pensados para el dueño del negocio (no programador).
 *
 * Editar la ayuda = editar ESTE archivo (nada de strings regados por los
 * componentes ni jerga en inglés). El shape es serializable a propósito: si un
 * día se quiere por instalación, se mueve tal cual a la config.
 */

/** Ayuda contextual por pantalla/sección (botones "?"). */
export const HELP_TEXTS: Record<string, { title: string; body: string }> = {
  dashboard: {
    title: 'Dashboard',
    body:
      'Resumen general del sistema. Aquí ve cuántas conversaciones y contactos tiene. ' +
      'Su día a día vive en Conversaciones (leads), Trabajos (citas) y Tracking (técnicos).',
  },
  conversations: {
    title: 'Conversaciones',
    body:
      'Todos los chats de WhatsApp. El asistente responde solo y clasifica cada lead por color: ' +
      'Caliente (listo para cerrar), Tibio (interesado) o Frío. Los chats que necesitan una persona ' +
      'quedan marcados — entre ahí y tome el control cuando haga falta.',
  },
  crm: {
    title: 'CRM · Pipeline',
    body:
      'Sus leads organizados por temperatura, como un embudo de ventas. Arrastre o cambie el estado ' +
      'cuando cierre o descarte. La ficha de cada cliente se abre con un clic.',
  },
  jobs: {
    title: 'Trabajos',
    body:
      'Los servicios agendados: a qué cliente, con qué técnico, a qué hora, y en qué van ' +
      '(en camino, en sitio, completado). Aquí también ve la respuesta del cliente a la encuesta. ' +
      'Los trabajos se crean desde la conversación del lead con el botón Agendar.',
  },
  tracking: {
    title: 'Tracking',
    body:
      'El mapa del día: dónde está cada técnico y cómo van los trabajos de hoy. ' +
      'Azul = en camino, verde = en sitio, rojo = se pasó del tiempo sin cerrar, gris = completado. ' +
      'La ubicación llega sola cuando el técnico marca Llegué o Terminé desde su teléfono.',
  },
  employees: {
    title: 'Empleados',
    body:
      'Su equipo. Cree la ficha de cada técnico con su teléfono para que reciba avisos, y ' +
      'créele acceso para que pueda marcar Llegué/Terminé desde su celular.',
  },
  contacts: {
    title: 'Contactos',
    body:
      'Todas las personas que han escrito. La ficha guarda lo que el asistente detectó ' +
      '(zona, modelo de interés) y sus notas internas.',
  },
  campaigns: {
    title: 'Campañas',
    body:
      'Envíos masivos de WhatsApp a una lista de números. Úselo con moderación: ' +
      'muchos mensajes seguidos a desconocidos pueden hacer que WhatsApp bloquee el número.',
  },
  agents: {
    title: 'Agentes AI',
    body:
      'El cerebro del asistente que responde los chats. Normalmente no necesita tocar nada aquí: ' +
      'la personalidad y el catálogo se editan en Instalación.',
  },
  channels: {
    title: 'Canales',
    body:
      'La conexión con WhatsApp. Si aparece desconectado, entre aquí y escanee el código QR ' +
      'con el teléfono del negocio (WhatsApp → Dispositivos vinculados).',
  },
  installation: {
    title: 'Instalación',
    body:
      'Todo lo que es SUYO del negocio: nombre, catálogo con precios y fotos, la personalidad ' +
      'del asistente, las opciones del CRM y los mensajes automáticos (recordatorios, encuesta). ' +
      'Lo que cambie aquí aplica de inmediato, sin programador.',
  },
  settings: {
    title: 'Configuración',
    body:
      'Ajustes del sistema: horario en que el asistente responde, número del dueño para alertas, ' +
      'y la clave de IA que hace funcionar al asistente.',
  },
  users: {
    title: 'Usuarios',
    body:
      'Quién puede entrar al panel. Invite con el botón de arriba: puede copiar el enlace de ' +
      'invitación y mandarlo por WhatsApp. Los técnicos solo ven sus trabajos, no el CRM.',
  },
};

/** Un paso del tour de bienvenida (elemento a resaltar + texto). */
export interface TourStepText {
  /** Selector del elemento a resaltar (data-tour en el sidebar). */
  element: string;
  title: string;
  description: string;
}

/**
 * Tour de primera vez: recorre el MENÚ lateral explicando el flujo diario.
 * Se auto-lanza en el primer login del admin y se repite desde Ayuda.
 */
export const TOUR_STEPS: TourStepText[] = [
  {
    element: '[data-tour="logo"]',
    title: 'Bienvenido a tu panel',
    description:
      'Este es su panel. En un minuto le mostramos dónde vive cada cosa de su día a día. ' +
      'Puede saltar el tour cuando quiera y volver a verlo desde el menú de Ayuda (?).',
  },
  {
    element: '[data-tour="/conversations"]',
    title: '1 · Conversaciones',
    description:
      'Aquí caen los chats de WhatsApp. El asistente responde solo y marca cada lead: ' +
      'Caliente, Tibio o Frío. Lo que necesite una persona queda resaltado.',
  },
  {
    element: '[data-tour="/jobs"]',
    title: '2 · Trabajos',
    description:
      'Cuando un lead cierra, se agenda aquí: cliente, técnico, fecha y hora. ' +
      'El sistema le recuerda la cita al cliente y le avisa cuando el técnico llega.',
  },
  {
    element: '[data-tour="/tracking"]',
    title: '3 · Tracking',
    description:
      'El mapa del día: dónde está cada técnico y cómo van los trabajos de hoy, con colores. ' +
      'Rojo = alguien lleva mucho tiempo sin cerrar — ahí es donde usted llama.',
  },
  {
    element: '[data-tour="/installation"]',
    title: '4 · Instalación',
    description:
      'Su negocio como dato: catálogo con precios y fotos, la personalidad del asistente y los ' +
      'mensajes automáticos. Cambie lo que necesite: aplica al momento, sin programador.',
  },
  {
    element: '[data-tour="help"]',
    title: 'Ayuda siempre a mano',
    description:
      'Cada pantalla tiene un botón "?" que explica qué hacer ahí. Y desde este menú puede ' +
      'volver a ver este tour cuando quiera. ¡Listo!',
  },
];
